'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Icon from '@/components/ui/AppIcon';

// ─── Types ────────────────────────────────────────────────────

interface TestResult {
  suite: string;
  name: string;
  category: string;
  status: 'pass' | 'fail' | 'warning' | 'skipped';
  duration_ms: number;
  message: string;
  recovery_recommendation: string;
}

interface SupplierTestSummary {
  supplier: string;
  supplierName: string;
  configured: boolean;
  total: number;
  passed: number;
  failed: number;
  warnings: number;
  skipped: number;
  healthScore: number;
  productionReady: boolean;
  overallStatus: 'passed' | 'failed' | 'warning' | 'running';
  tests: TestResult[];
}

interface SupplierRow {
  slug: string;
  name: string;
  category: string;
  configured: boolean;
  latestRun: {
    id: string;
    started_at: string;
    total_tests: number;
    passed_tests: number;
    failed_tests: number;
    warning_tests: number;
    skipped_tests: number;
    overall_status: string;
    health_score: number;
    production_ready: boolean;
  } | null;
}

const CATEGORY_COLORS: Record<string, string> = {
  activities: 'bg-amber-500/15 text-amber-400',
  flights: 'bg-blue-500/15 text-blue-400',
  hotels: 'bg-emerald-500/15 text-emerald-400',
  cruises: 'bg-cyan-500/15 text-cyan-400',
  multi: 'bg-violet-500/15 text-violet-400',
};

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string; icon: string }> = {
  passed: { label: 'All Tests Passed', color: 'text-emerald-400', bg: 'bg-emerald-500/15', icon: 'CheckCircleIcon' },
  failed: { label: 'Tests Failed', color: 'text-red-400', bg: 'bg-red-500/15', icon: 'XCircleIcon' },
  warning: { label: 'Warnings', color: 'text-amber-400', bg: 'bg-amber-500/15', icon: 'ExclamationTriangleIcon' },
  running: { label: 'Running', color: 'text-blue-400', bg: 'bg-blue-500/15', icon: 'ArrowPathIcon' },
  not_run: { label: 'Not Run', color: 'text-muted-foreground', bg: 'bg-surface-3', icon: 'MinusCircleIcon' },
};

const TEST_CATEGORY_ICONS: Record<string, string> = {
  auth: 'KeyIcon',
  connectivity: 'SignalIcon',
  endpoint: 'ServerIcon',
  webhook: 'BoltIcon',
  sync: 'ArrowPathIcon',
  version: 'CodeBracketIcon',
  performance: 'BoltIcon',
};

const SUITES = ['authentication', 'connectivity', 'endpoints', 'webhooks', 'synchronization', 'api_version', 'performance'];

function ScoreRing({ score, size = 56 }: { score: number; size?: number }) {
  const r = (size - 8) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (score / 100) * circ;
  const color = score >= 90 ? '#34d399' : score >= 70 ? '#fbbf24' : '#f87171';
  return (
    <svg width={size} height={size} className="flex-shrink-0">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="currentColor" strokeWidth={4} className="text-surface-4" />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={4}
        strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`} />
      <text x={size / 2} y={size / 2 + 5} textAnchor="middle" fill={color} fontSize={size < 50 ? 11 : 13} fontWeight="bold">{score}</text>
    </svg>
  );
}

export default function SupplierTestingContent() {
  const [suppliers, setSuppliers] = useState<SupplierRow[]>([]);
  const [testResults, setTestResults] = useState<SupplierTestSummary[]>([]);
  const [selectedSupplier, setSelectedSupplier] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [runningSupplier, setRunningSupplier] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'details' | 'recommendations'>('overview');
  const [filterCategory, setFilterCategory] = useState('all');

  const loadSummary = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/supplier-testing?type=summary');
      const data = await res.json();
      setSuppliers(data.suppliers || []);
    } catch {
      // fallback handled below
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  const runTests = async (supplierSlug?: string) => {
    if (supplierSlug) {
      setRunningSupplier(supplierSlug);
    } else {
      setRunning(true);
    }
    try {
      const res = await fetch('/api/supplier-testing', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'run_tests', supplier_slug: supplierSlug || undefined }),
      });
      const data = await res.json();
      if (data.results) {
        setTestResults(data.results);
        if (supplierSlug) {
          setSelectedSupplier(supplierSlug);
          setActiveTab('details');
        }
        // Refresh summary
        await loadSummary();
      }
    } finally {
      setRunning(false);
      setRunningSupplier(null);
    }
  };

  const selectedResult = testResults.find((r) => r.supplier === selectedSupplier);
  const filteredSuppliers = filterCategory === 'all' ? suppliers : suppliers.filter((s) => s.category === filterCategory);

  const overallStats = testResults.length > 0 ? {
    totalSuppliers: testResults.length,
    productionReady: testResults.filter((r) => r.productionReady).length,
    totalTests: testResults.reduce((s, r) => s + r.total, 0),
    totalPassed: testResults.reduce((s, r) => s + r.passed, 0),
    totalFailed: testResults.reduce((s, r) => s + r.failed, 0),
    avgHealthScore: Math.round(testResults.reduce((s, r) => s + r.healthScore, 0) / testResults.length),
  } : null;

  const allRecommendations = testResults.flatMap((r) =>
    r.tests.filter((t) => t.recovery_recommendation && (t.status === 'fail' || t.status === 'warning'))
      .map((t) => ({ supplier: r.supplierName, test: t.name, status: t.status, recommendation: t.recovery_recommendation }))
  );

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <div className="bg-surface-1 border-b border-border px-6 py-5">
        <div className="max-w-screen-2xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-3">
              <span className="w-9 h-9 rounded-xl bg-violet-500/15 flex items-center justify-center">
                <Icon name="BeakerIcon" className="w-5 h-5 text-violet-400" />
              </span>
              Automated Supplier Testing & Validation
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Comprehensive testing for all 15 suppliers — auth, connectivity, endpoints, webhooks, sync, and performance
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => runTests()} disabled={running}
              className="px-5 py-2.5 bg-violet-600 text-white rounded-lg text-sm font-medium hover:bg-violet-700 transition-colors flex items-center gap-2 disabled:opacity-60">
              <Icon name={running ? 'ArrowPathIcon' : 'PlayIcon'} className={`w-4 h-4 ${running ? 'animate-spin' : ''}`} />
              {running ? 'Running All Tests...' : 'Run All Tests'}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-screen-2xl mx-auto px-6 py-6">
        {/* Tabs */}
        <div className="flex gap-1 bg-surface-2 rounded-xl p-1 mb-6 w-fit">
          {[
            { id: 'overview', label: 'Supplier Overview', icon: 'TableCellsIcon' },
            { id: 'details', label: 'Test Details', icon: 'ListBulletIcon' },
            { id: 'recommendations', label: 'Recovery Recommendations', icon: 'LightBulbIcon' },
          ].map((tab) => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === tab.id ? 'bg-surface-1 text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
              <Icon name={tab.icon} className="w-4 h-4" />
              {tab.label}
              {tab.id === 'recommendations' && allRecommendations.length > 0 && (
                <span className="w-5 h-5 rounded-full bg-amber-500/20 text-amber-400 text-xs flex items-center justify-center">{allRecommendations.length}</span>
              )}
            </button>
          ))}
        </div>

        {/* ── Overview Tab ── */}
        {activeTab === 'overview' && (
          <div className="space-y-5">
            {/* Stats bar */}
            {overallStats && (
              <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                {[
                  { label: 'Suppliers Tested', value: overallStats.totalSuppliers, color: 'text-foreground' },
                  { label: 'Production Ready', value: overallStats.productionReady, color: 'text-emerald-400' },
                  { label: 'Total Tests', value: overallStats.totalTests, color: 'text-foreground' },
                  { label: 'Passed', value: overallStats.totalPassed, color: 'text-emerald-400' },
                  { label: 'Failed', value: overallStats.totalFailed, color: 'text-red-400' },
                  { label: 'Avg Health Score', value: `${overallStats.avgHealthScore}%`, color: overallStats.avgHealthScore >= 80 ? 'text-emerald-400' : 'text-amber-400' },
                ].map((s) => (
                  <div key={s.label} className="bg-surface-2 border border-border rounded-xl p-3 text-center">
                    <div className={`text-xl font-bold ${s.color}`}>{s.value}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{s.label}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Category filter */}
            <div className="flex gap-2 flex-wrap">
              {['all', 'activities', 'flights', 'hotels', 'cruises', 'multi'].map((cat) => (
                <button key={cat} onClick={() => setFilterCategory(cat)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filterCategory === cat ? 'bg-primary/20 text-primary border border-primary/40' : 'bg-surface-2 text-muted-foreground border border-border hover:border-primary/30'}`}>
                  {cat.charAt(0).toUpperCase() + cat.slice(1)}
                </button>
              ))}
            </div>

            {/* Supplier grid */}
            {loading ? (
              <div className="text-center py-12 text-muted-foreground">Loading supplier data...</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {filteredSuppliers.map((supplier) => {
                  const result = testResults.find((r) => r.supplier === supplier.slug);
                  const run = supplier.latestRun;
                  const healthScore = result?.healthScore ?? run?.health_score ?? 0;
                  const status = result?.overallStatus ?? run?.overall_status ?? 'not_run';
                  const statusCfg = STATUS_CONFIG[status] || STATUS_CONFIG.not_run;
                  const catColor = CATEGORY_COLORS[supplier.category] || 'bg-surface-3 text-muted-foreground';
                  const isRunning = runningSupplier === supplier.slug;

                  return (
                    <div key={supplier.slug} className="bg-surface-2 border border-border rounded-xl p-4 hover:border-primary/30 transition-colors">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-foreground">{supplier.name}</span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${catColor}`}>{supplier.category}</span>
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`text-xs font-medium ${supplier.configured ? 'text-emerald-400' : 'text-amber-400'}`}>
                              {supplier.configured ? '● Configured' : '○ Awaiting Credentials'}
                            </span>
                            {(result?.productionReady || run?.production_ready) && (
                              <span className="text-xs text-emerald-400 font-medium">✓ Production Ready</span>
                            )}
                          </div>
                        </div>
                        <ScoreRing score={healthScore} size={52} />
                      </div>

                      {(result || run) && (
                        <div className="grid grid-cols-4 gap-2 mb-3">
                          {[
                            { label: 'Pass', value: result?.passed ?? run?.passed_tests ?? 0, color: 'text-emerald-400' },
                            { label: 'Fail', value: result?.failed ?? run?.failed_tests ?? 0, color: 'text-red-400' },
                            { label: 'Warn', value: result?.warnings ?? run?.warning_tests ?? 0, color: 'text-amber-400' },
                            { label: 'Skip', value: result?.skipped ?? run?.skipped_tests ?? 0, color: 'text-muted-foreground' },
                          ].map((s) => (
                            <div key={s.label} className="bg-surface-3 rounded-lg p-2 text-center">
                              <div className={`text-sm font-bold ${s.color}`}>{s.value}</div>
                              <div className="text-xs text-muted-foreground">{s.label}</div>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="flex items-center justify-between">
                        <span className={`flex items-center gap-1.5 text-xs font-medium ${statusCfg.color}`}>
                          <Icon name={statusCfg.icon} className={`w-3.5 h-3.5 ${isRunning ? 'animate-spin' : ''}`} />
                          {isRunning ? 'Running...' : statusCfg.label}
                        </span>
                        <div className="flex gap-2">
                          {result && (
                            <button onClick={() => { setSelectedSupplier(supplier.slug); setActiveTab('details'); }}
                              className="px-2.5 py-1.5 bg-surface-3 text-foreground rounded-lg text-xs hover:bg-surface-4 transition-colors">
                              View
                            </button>
                          )}
                          <button onClick={() => runTests(supplier.slug)} disabled={isRunning || running}
                            className="px-2.5 py-1.5 bg-violet-500/15 text-violet-400 rounded-lg text-xs hover:bg-violet-500/25 transition-colors disabled:opacity-50 flex items-center gap-1">
                            <Icon name={isRunning ? 'ArrowPathIcon' : 'PlayIcon'} className={`w-3 h-3 ${isRunning ? 'animate-spin' : ''}`} />
                            Test
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── Details Tab ── */}
        {activeTab === 'details' && (
          <div className="space-y-4">
            {testResults.length === 0 ? (
              <div className="text-center py-16 bg-surface-2 rounded-xl border border-border">
                <Icon name="BeakerIcon" className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-foreground font-medium">No test results yet</p>
                <p className="text-sm text-muted-foreground mt-1 mb-4">Run tests to see detailed results</p>
                <button onClick={() => runTests()} disabled={running}
                  className="px-5 py-2.5 bg-violet-600 text-white rounded-lg text-sm font-medium hover:bg-violet-700 transition-colors">
                  Run All Tests
                </button>
              </div>
            ) : (
              <>
                {/* Supplier selector */}
                <div className="flex gap-2 flex-wrap">
                  {testResults.map((r) => (
                    <button key={r.supplier} onClick={() => setSelectedSupplier(r.supplier)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${selectedSupplier === r.supplier ? 'bg-primary/20 text-primary border border-primary/40' : 'bg-surface-2 text-muted-foreground border border-border hover:border-primary/30'}`}>
                      {r.supplierName}
                      <span className={`ml-1.5 ${r.failed > 0 ? 'text-red-400' : r.warnings > 0 ? 'text-amber-400' : 'text-emerald-400'}`}>
                        {r.failed > 0 ? `${r.failed}F` : r.warnings > 0 ? `${r.warnings}W` : '✓'}
                      </span>
                    </button>
                  ))}
                </div>

                {selectedResult && (
                  <div className="space-y-4">
                    {/* Summary header */}
                    <div className="bg-surface-2 border border-border rounded-xl p-5">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-4">
                          <ScoreRing score={selectedResult.healthScore} size={64} />
                          <div>
                            <h3 className="text-lg font-bold text-foreground">{selectedResult.supplierName}</h3>
                            <div className="flex items-center gap-2 mt-1">
                              <span className={`text-sm font-medium ${selectedResult.productionReady ? 'text-emerald-400' : 'text-amber-400'}`}>
                                {selectedResult.productionReady ? '✓ Production Ready' : '⚠ Not Production Ready'}
                              </span>
                              <span className={`text-sm ${selectedResult.configured ? 'text-emerald-400' : 'text-amber-400'}`}>
                                {selectedResult.configured ? '● Configured' : '○ Awaiting Credentials'}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="grid grid-cols-4 gap-3 text-center">
                          {[
                            { label: 'Passed', value: selectedResult.passed, color: 'text-emerald-400' },
                            { label: 'Failed', value: selectedResult.failed, color: 'text-red-400' },
                            { label: 'Warnings', value: selectedResult.warnings, color: 'text-amber-400' },
                            { label: 'Skipped', value: selectedResult.skipped, color: 'text-muted-foreground' },
                          ].map((s) => (
                            <div key={s.label} className="bg-surface-3 rounded-lg p-3">
                              <div className={`text-xl font-bold ${s.color}`}>{s.value}</div>
                              <div className="text-xs text-muted-foreground">{s.label}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Tests by suite */}
                    {SUITES.map((suite) => {
                      const suiteTests = selectedResult.tests.filter((t) => t.suite === suite);
                      if (suiteTests.length === 0) return null;
                      const suitePassed = suiteTests.filter((t) => t.status === 'pass').length;
                      const suiteFailed = suiteTests.filter((t) => t.status === 'fail').length;
                      const suiteWarnings = suiteTests.filter((t) => t.status === 'warning').length;
                      return (
                        <div key={suite} className="bg-surface-2 border border-border rounded-xl overflow-hidden">
                          <div className="px-5 py-3 border-b border-border flex items-center justify-between">
                            <h4 className="font-semibold text-foreground capitalize">{suite.replace('_', ' ')} Tests</h4>
                            <div className="flex gap-2 text-xs">
                              <span className="text-emerald-400">{suitePassed} pass</span>
                              {suiteFailed > 0 && <span className="text-red-400">{suiteFailed} fail</span>}
                              {suiteWarnings > 0 && <span className="text-amber-400">{suiteWarnings} warn</span>}
                            </div>
                          </div>
                          <div className="divide-y divide-border/50">
                            {suiteTests.map((test, i) => {
                              const statusColors: Record<string, string> = {
                                pass: 'text-emerald-400 bg-emerald-500/10',
                                fail: 'text-red-400 bg-red-500/10',
                                warning: 'text-amber-400 bg-amber-500/10',
                                skipped: 'text-muted-foreground bg-surface-3',
                              };
                              const statusIcons: Record<string, string> = {
                                pass: 'CheckCircleIcon',
                                fail: 'XCircleIcon',
                                warning: 'ExclamationTriangleIcon',
                                skipped: 'MinusCircleIcon',
                              };
                              return (
                                <div key={i} className="px-5 py-3 flex items-start gap-3 hover:bg-surface-3/30 transition-colors">
                                  <Icon name={statusIcons[test.status] || 'MinusCircleIcon'} className={`w-4 h-4 mt-0.5 flex-shrink-0 ${statusColors[test.status]?.split(' ')[0] || 'text-muted-foreground'}`} />
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm font-medium text-foreground">{test.name}</span>
                                      <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${statusColors[test.status] || 'text-muted-foreground bg-surface-3'}`}>
                                        {test.status}
                                      </span>
                                      {test.duration_ms > 0 && <span className="text-xs text-muted-foreground">{test.duration_ms}ms</span>}
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-0.5">{test.message}</p>
                                    {test.recovery_recommendation && (
                                      <p className="text-xs text-amber-400/80 mt-1 flex items-start gap-1">
                                        <Icon name="LightBulbIcon" className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                        {test.recovery_recommendation}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ── Recommendations Tab ── */}
        {activeTab === 'recommendations' && (
          <div className="space-y-4">
            {allRecommendations.length === 0 ? (
              <div className="text-center py-16 bg-surface-2 rounded-xl border border-border">
                <Icon name="CheckCircleIcon" className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
                <p className="text-foreground font-medium">No recovery recommendations</p>
                <p className="text-sm text-muted-foreground mt-1">All tests passed — no action required</p>
              </div>
            ) : (
              <>
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex items-start gap-3">
                  <Icon name="LightBulbIcon" className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-300">{allRecommendations.length} Recovery Recommendation{allRecommendations.length !== 1 ? 's' : ''}</p>
                    <p className="text-xs text-amber-400/80 mt-0.5">Address these issues to improve supplier health and production readiness</p>
                  </div>
                </div>

                <div className="space-y-3">
                  {allRecommendations.map((rec, i) => (
                    <div key={i} className={`bg-surface-2 border rounded-xl p-4 ${rec.status === 'fail' ? 'border-red-500/30' : 'border-amber-500/30'}`}>
                      <div className="flex items-start gap-3">
                        <span className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${rec.status === 'fail' ? 'bg-red-500/15' : 'bg-amber-500/15'}`}>
                          <Icon name={rec.status === 'fail' ? 'XCircleIcon' : 'ExclamationTriangleIcon'} className={`w-4 h-4 ${rec.status === 'fail' ? 'text-red-400' : 'text-amber-400'}`} />
                        </span>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-foreground text-sm">{rec.supplier}</span>
                            <span className="text-xs text-muted-foreground">·</span>
                            <span className="text-sm text-muted-foreground">{rec.test}</span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${rec.status === 'fail' ? 'bg-red-500/15 text-red-400' : 'bg-amber-500/15 text-amber-400'}`}>
                              {rec.status}
                            </span>
                          </div>
                          <p className="text-sm text-foreground/80 mt-2 flex items-start gap-2">
                            <Icon name="LightBulbIcon" className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                            {rec.recommendation}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
